import { loadAndTrack } from "redux-request-loading";

import { Payload } from "@neworbit/redux-helpers";

import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { questionnaireLoaded, saveQuestionnaireSuccess, submitQuestionnaireSuccess } from "@teamprevent/common/questionnaires/actions";
import { toast } from "@teamprevent/common/toasts";
import { MHP_TREATMENT_REPORT_KEY } from "@teamprevent/common/mentalHealthPractitionerTreatment/models";

import { actionTaskSuccess, createOhCaseSuccess } from "../crud/ohCase/actions";
import { Dispatch } from "../store";
import { submitClinicalNotesSuccess } from "../clinicalNotes/actions";
import { AddTaskModel, CreateTaskDto, TasksUpdatedSuccessResult } from "../task/model";
import { OhCaseDetailModel } from "../crud/ohCase/model";

import {
    APPROVE_MHP_TREATMENT_REPORT,
    APPROVE_MHP_TREATMENT_REPORT_SUCCESS,
    CREATE_MHP_TREATMENT_CASE,
    LOAD_MHP_TREATMENT_CONSULTATION,
    LOAD_MHP_TREATMENT_REPORT,
    LOAD_MHP_TREATMENT_REPORT_PREVIEW,
    REJECT_MHP_TREATMENT_REPORT,
    REJECT_MHP_TREATMENT_REPORT_SUCCESS,
    REQUEST_CHANGES_MHP_TREATMENT_REPORT,
    REQUEST_CHANGES_MHP_TREATMENT_REPORT_SUCCESS,
    SAVE_MHP_TREATMENT_CONSULTATION,
    SAVE_MHP_TREATMENT_REPORT,
    SUBMIT_MHP_TREATMENT_CONSULTATION,
    SUBMIT_MHP_TREATMENT_REPORT,
    SUBMIT_MHP_TREATMENT_REPORT_SUCCESS
} from "./actionTypes";
import { mhpTreatmentApi } from "./mhpTreatmentApi";
import { mhpTreatmentCaseApi } from "./mhpTreatmentCaseApi";
import { CreateMHPCaseTreatmentModel, MHP_TREATMENT_CONSULTATION } from "./models";

export type MHPTreatmentAction =
    MHPTreatmentReportSubmitSuccess |
    ApproveMHPTreatmentReportSuccess |
    RequestMHPTreatmentReportChangesSuccess |
    RejectMHPTreatmentReportReportSuccess;

export type MHPTreatmentReportSubmitSuccess = ({ type: typeof SUBMIT_MHP_TREATMENT_REPORT_SUCCESS }) & Payload<OhCaseDetailModel>;
export type ApproveMHPTreatmentReportSuccess = ({ type: typeof APPROVE_MHP_TREATMENT_REPORT_SUCCESS }) & Payload<OhCaseDetailModel>;
export type RequestMHPTreatmentReportChangesSuccess = ({ type: typeof REQUEST_CHANGES_MHP_TREATMENT_REPORT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;
export type RejectMHPTreatmentReportReportSuccess = ({ type: typeof REJECT_MHP_TREATMENT_REPORT_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;

export const createMHPTreatmentCase = (createModel: CreateMHPCaseTreatmentModel) =>
    async (dispatch: Dispatch) => {
        const createdOhCase = await loadAndTrack(dispatch, CREATE_MHP_TREATMENT_CASE, mhpTreatmentCaseApi.create(createModel));
        dispatch(createOhCaseSuccess(createdOhCase));
        return createdOhCase;
    };

export const loadMHPTreatmentConsultation = (ohCaseId: string, taskId: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, LOAD_MHP_TREATMENT_CONSULTATION, mhpTreatmentApi.loadConsultation(ohCaseId, taskId));
        const questions = response?.data?.questions || [];
        dispatch(questionnaireLoaded({ questions, questionnaireKey: MHP_TREATMENT_CONSULTATION, eTag: response?.eTag }));
    };
};

export const saveMHPTreatmentConsultation = (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, SAVE_MHP_TREATMENT_CONSULTATION, mhpTreatmentApi.saveConsultation(ohCaseId, taskId, questions, eTag));
        dispatch(saveQuestionnaireSuccess({ questionnaireKey: MHP_TREATMENT_CONSULTATION, eTag: response.eTag }));
    };
};

export const submitMHPTreatmentConsultation = (ohCaseId: string, taskId: string, nextTasks: CreateTaskDto[]) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, SUBMIT_MHP_TREATMENT_CONSULTATION, mhpTreatmentApi.submitConsultation(ohCaseId, taskId, nextTasks));
        toast.success("Clinical Notes submitted successfully");
        dispatch(submitQuestionnaireSuccess(MHP_TREATMENT_CONSULTATION));
        dispatch(submitClinicalNotesSuccess({
            ohCaseId,
            tasks: result
        }));
    };
};

export const loadMHPTreatmentReport = (ohCaseId: string, taskId: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, LOAD_MHP_TREATMENT_REPORT, mhpTreatmentApi.loadReport(ohCaseId, taskId));
        const questions = response?.data?.questions || [];
        dispatch(questionnaireLoaded({
            questions,
            questionnaireKey: MHP_TREATMENT_REPORT_KEY,
            eTag: response?.eTag,
            isDefaultCustomerType: response?.isDefaultCustomerType,
            customerType: response?.customerType,
            reportChangesBlocked: response?.reportChangesBlocked,
            releaseReportComments: response?.releaseReportComments,
            approvalState: response?.approvalState,
            isConsentWithdrawn: response?.isConsentWithdrawn
        }));
    };
};

export const loadMHPTreatmentReportPreview = (ohCaseId: string, taskId: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, LOAD_MHP_TREATMENT_REPORT_PREVIEW, mhpTreatmentApi.loadReportPreview(ohCaseId, taskId));
        const questions = response?.data?.questions || [];
        dispatch(questionnaireLoaded({
            questions,
            questionnaireKey: MHP_TREATMENT_REPORT_KEY,
            eTag: response?.eTag,
            isDefaultCustomerType: response?.isDefaultCustomerType,
            customerType: response?.customerType
        }));
    };
};

export const saveMHPTreatmentReport = (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, SAVE_MHP_TREATMENT_REPORT, mhpTreatmentApi.saveReport(ohCaseId, taskId, questions, eTag));
        dispatch(saveQuestionnaireSuccess({ questionnaireKey: MHP_TREATMENT_REPORT_KEY, eTag: response.eTag }));
    };
};

export const submitMHPTreatmentReport = (ohCaseId: string, taskId: string, createTasks: AddTaskModel[]) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(
            dispatch, SUBMIT_MHP_TREATMENT_REPORT, mhpTreatmentApi.submitReport(ohCaseId, taskId, createTasks));

        if (result) {
            toast.success("MHP Treatment Report submitted successfully");
            dispatch(submitMHPTreatmentReportSuccess(result));
        }
    };
};

export const submitMHPTreatmentReportAmendment = (ohCaseId: string, taskId: string, createTasks: AddTaskModel[]) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(
            dispatch, SUBMIT_MHP_TREATMENT_REPORT, mhpTreatmentApi.submitReportAmendment(ohCaseId, taskId, createTasks));

        if (result) {
            toast.success("MHP Treatment Report submitted successfully");
            dispatch(submitMHPTreatmentReportSuccess(result));
        }
    };
};

export const submitMHPTreatmentReportSuccess = (payload: OhCaseDetailModel): MHPTreatmentReportSubmitSuccess => {
    return {
        type: SUBMIT_MHP_TREATMENT_REPORT_SUCCESS,
        payload
    };
};

export const approveMHPTreatmentReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, APPROVE_MHP_TREATMENT_REPORT, mhpTreatmentApi.approveReport(ohCaseId, taskId, notes));
        dispatch(approveMHPTreatmentReportSuccess(result));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: result.tasks ?? [] }));
    };
};

export const approveMHPTreatmentReportSuccess = (payload: OhCaseDetailModel): ApproveMHPTreatmentReportSuccess => {
    return {
        type: APPROVE_MHP_TREATMENT_REPORT_SUCCESS,
        payload
    };
};

export const requestMHPTreatmentReportChanges = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const results =
            await loadAndTrack(dispatch, REQUEST_CHANGES_MHP_TREATMENT_REPORT, mhpTreatmentApi.requestReportChanges(ohCaseId, taskId, notes));
        dispatch(requestMHPTreatmentReportChangesSuccess({ ohCaseId, tasks: results }));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: results }));
    };
};

export const requestMHPTreatmentReportChangesSuccess = (payload: TasksUpdatedSuccessResult): RequestMHPTreatmentReportChangesSuccess => {
    return {
        type: REQUEST_CHANGES_MHP_TREATMENT_REPORT_SUCCESS,
        payload
    };
};

export const rejectMHPTreatmentReport = (ohCaseId: string, taskId: string, notes: string) => {
    return async (dispatch: Dispatch) => {
        const results = await loadAndTrack(dispatch, REJECT_MHP_TREATMENT_REPORT, mhpTreatmentApi.rejectReport(ohCaseId, taskId, notes));
        dispatch(rejectMHPTreatmentReportSuccess({ ohCaseId, tasks: results }));
        dispatch(actionTaskSuccess({ ohCaseId, tasks: results }));
    };
};

export const rejectMHPTreatmentReportSuccess = (payload: TasksUpdatedSuccessResult): RejectMHPTreatmentReportReportSuccess => {
    return {
        type: REJECT_MHP_TREATMENT_REPORT_SUCCESS,
        payload
    };
};
